import React from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useLanguage } from '../../features/language/useLanguage';

/**
 * The one and only tour card. It never performs the step's action — the user
 * clicks the highlighted real control for that. The card only carries
 * navigation: Prev, Next, Skip.
 */
interface OnboardingCoachCardProps {
  stepNumber: number;
  totalSteps: number;
  module: string;
  body: React.ReactNode;
  hint?: React.ReactNode;
  nextLabel?: string;
  nextDisabled?: boolean;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  busy?: boolean;
}

export const OnboardingCoachCard: React.FC<OnboardingCoachCardProps> = ({
  stepNumber,
  totalSteps,
  module,
  body,
  hint,
  nextLabel,
  nextDisabled = false,
  onNext,
  onPrev,
  onSkip,
  busy = false,
}) => {
  const { t } = useLanguage();
  return (
    <div
      className="w-full rounded-xl border border-amber-200 bg-white p-4 shadow-2xl"
      data-test-id="onboarding-panel"
    >
      <div className="flex items-center gap-2">
        <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-amber-600 px-1.5 text-xs font-bold text-white">
          {stepNumber}
        </span>
        <span className="text-xs font-semibold uppercase tracking-wide text-amber-700">
          {t(
            'onboarding.coach.stepLabel',
            { step: stepNumber, total: totalSteps, module },
            'Step {{step}} of {{total}} · {{module}}',
          )}
        </span>
      </div>

      <p className="mt-2 text-sm text-gray-800">{body}</p>
      {hint && <p className="mt-1 text-xs font-medium text-amber-700">{hint}</p>}

      <div className="mt-4 flex items-center justify-between">
        <button
          type="button"
          data-test-id="onboarding-prev"
          onClick={onPrev}
          disabled={busy}
          className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 disabled:opacity-40"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> {t('onboarding.coach.back', undefined, 'Back')}
        </button>

        <div className="flex items-center gap-3">
          <button
            type="button"
            data-test-id="onboarding-skip"
            onClick={onSkip}
            disabled={busy}
            className="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-40"
          >
            {t('onboarding.coach.skip', undefined, 'Skip tour')}
          </button>
          <button
            type="button"
            data-test-id="onboarding-next"
            onClick={onNext}
            disabled={nextDisabled || busy}
            className={cn(
              'inline-flex items-center gap-1 rounded-lg px-4 py-1.5 text-sm font-medium text-white transition-colors',
              nextDisabled || busy
                ? 'cursor-not-allowed bg-gray-300'
                : 'bg-amber-600 hover:bg-amber-700 onboarding-btn-glow',
            )}
          >
            {busy ? t('onboarding.coach.working', undefined, 'Working…') : (nextLabel ?? t('onboarding.coach.next', undefined, 'Next'))}
            {!busy && <ArrowRight className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
};
